import { Asset, AssetRepository, GetAssetByIdOrSlugParams, GetAssetsParams, GetAssetsResponse, Meta } from '../types'
import { MeilisearchAsset } from '@/lib/meilisearch/types'
import {
  assetTypeToMeilisearchVerticalType,
  mainCategorySlugToMeilisearchVertical,
  meilisearchAssetToAssetSummary,
} from '@/lib/meilisearch/utils'
import { ITEMS_PER_PAGE, MAX_ASSETS_FILTERS_TAGS } from '@/lib/constants'
import { MEILISEARCH_INDEX_PREFIX } from '@/lib/config'
import client from '@/lib/meilisearch/client'
import {
  Category,
  Content,
  getCategoryByIdOrSlug,
  getContentByIdOrSlug,
  getContents,
} from '@/lib/categories/categories'
import { FederatedMultiSearchParams, Filter, MultiSearchQuery } from 'meilisearch'

export class MeilisearchAssetRepository implements AssetRepository {
  async getAssets({
    mainCategorySlug,
    categorySlug,
    subCategorySlug,
    contentSlug,
    page,
    search,
    premium,
    sort,
    tags,
    signal,
    contentsLimit,
  }: GetAssetsParams): Promise<GetAssetsResponse> {
    // Category tags strategy:
    // The strategy is to match the category path formed by the main category, category, subCategory and content slugs
    // and try to match the assets that belong to that path using the `categories_path` attribute
    // const categoryTags = [mainCategorySlug, categorySlug,
    // subCategorySlug, contentSlug].filter(Boolean).join('/')
    // const categoryFilters = ['category_tags EXISTS', `category_tags IN ["${categoryTags}"]`]

    const indexName = `${MEILISEARCH_INDEX_PREFIX}assets`

    // Here we'll store the queries in case there's more than one
    const queries: Array<{
      query: MultiSearchQuery
      facetFilter: Filter
    }> = []

    // --- Search filter ---

    const query = search ? search.trim() : ''

    // --- Vertical filter ---

    const vertical = mainCategorySlug ? mainCategorySlugToMeilisearchVertical(mainCategorySlug) : null
    const verticalFilter = vertical ? `vertical = "${vertical}"` : ''

    // --- Vertical type filter ---

    // For 3d vertical, if we can resolve a category, use its "asset_type" to build the vertical_type filter
    const category: Category | null = categorySlug
      ? await getCategoryByIdOrSlug(categorySlug, {
          mainCategoryIdOrSlug: mainCategorySlug,
        })
      : null

    const verticalType = category?.assetType ? assetTypeToMeilisearchVerticalType(category?.assetType) : null
    const verticalTypeFilter = verticalType ? `vertical_type = "${verticalType}"` : ''

    // --- Premium filter ---

    const premiumFilter = premium !== undefined ? [`level = ${premium ? 1 : 0}`] : []

    // --- Tags filter ---

    const tagsFilter = (tags ?? []).map((tag) => `tags = "${tag}"`).join(' OR ')

    // --- Sorting ---

    const sortQuery: string[] = []
    if (sort) {
      switch (sort) {
        case 'new':
          sortQuery.push('created_at_timestamp:desc')
          break
        case 'popular':
          sortQuery.push('downloads_ranking.year:desc')
          break
        case 'trending':
          sortQuery.push('downloads_ranking.month:desc')
          break
      }
    }

    // --- Contents filter ---

    // Determine if we have enough information to resolve the content
    const shouldResolveContent = !!mainCategorySlug && !!categorySlug && !!subCategorySlug

    if (shouldResolveContent) {
      // If we have more than one content, we need to add a query for each one
      let content: Content | null = null

      if (contentSlug) {
        content = await getContentByIdOrSlug(contentSlug, {
          mainCategoryIdOrSlug: mainCategorySlug,
          categoryIdOrSlug: categorySlug,
          subCategoryIdOrSlug: subCategorySlug,
        })
      }

      let contents: Content[] = content
        ? [content]
        : await getContents({
            mainCategoryIdOrSlug: mainCategorySlug,
            subCategoryIdOrSlug: subCategorySlug,
            categoryIdOrSlug: categorySlug,
          })

      if (contentsLimit) {
        console.debug('Limiting contents to', contentsLimit)
        contents = contents.slice(0, contentsLimit)
      }

      contents.forEach((content) => {
        // Contents with search tag
        if (content.searchTag) {
          const searchTagFilter = content.searchTag
            .replaceAll('+', ' ')
            .split(' ')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)
            .map((tag) => `tags IN ["${tag.toLowerCase()}"]`)
            .join(' AND ')
          if (searchTagFilter.length > 0) {
            queries.push({
              query: {
                indexUid: indexName,
                q: query,
                filter: [verticalFilter, verticalTypeFilter, ...premiumFilter, searchTagFilter, tagsFilter].filter(
                  (filter) => filter.trim().length > 0
                ),
                sort: sortQuery,
              },
              facetFilter: [verticalFilter, verticalTypeFilter, ...premiumFilter, searchTagFilter].filter(
                (filter) => filter.trim().length > 0
              ),
            })
          }
        }
        // Contents with a search query
        else if (content.searchQuery && content.searchQuery.trim().length > 0) {
          queries.push({
            query: {
              indexUid: indexName,
              q: [query, content.searchQuery.trim()].filter((q) => q.length > 0).join(' '),
              filter: [verticalFilter, verticalTypeFilter, ...premiumFilter, tagsFilter].filter(
                (filter) => filter.trim().length > 0
              ),
              sort: sortQuery,
            },
            facetFilter: [verticalFilter, verticalTypeFilter, ...premiumFilter].filter(
              (filter) => filter.trim().length > 0
            ),
          })
        }
      })
    }

    // --- Search assets ---

    const limit = ITEMS_PER_PAGE
    const currentPage = page ?? 1
    const offset = (currentPage - 1) * ITEMS_PER_PAGE

    let totalItems = 0
    let totalPages = 0
    let hits: MeilisearchAsset[] = []
    let resultTags: string[] = []

    const isMultiSearch = queries.length > 1

    if (isMultiSearch) {
      const results = await client.multiSearch<FederatedMultiSearchParams, MeilisearchAsset>(
        {
          federation: {
            limit,
            offset,
            facetsByIndex: {
              [indexName]: ['tags'],
            },
          },
          queries: queries.map(({ query }) => query),
        },
        {
          signal,
        }
      )

      totalItems = results.estimatedTotalHits ?? 0
      totalPages = totalItems > 0 ? Math.ceil(totalItems / ITEMS_PER_PAGE) : 0
      hits = results.hits

      // TODO: Implement facets for multiple queries
    } else {
      // If there's only one query, we can use the first one. If not, we'll use a default search query
      const resolvedQuery = queries[0] ?? {
        query: {
          indexUid: indexName,
          q: query,
          filter: [verticalFilter, verticalTypeFilter, ...premiumFilter, tagsFilter].filter(
            (filter) => filter.trim().length > 0
          ),
          sort: sortQuery,
        },
        facetFilter: [verticalFilter, verticalTypeFilter, ...premiumFilter].filter(
          (filter) => filter.trim().length > 0
        ),
      }

      const index = client.index(resolvedQuery.query.indexUid)

      const results = await index.search<MeilisearchAsset>(
        resolvedQuery.query.q,
        {
          filter: resolvedQuery.query.filter,
          limit,
          offset,
          sort: sortQuery,
        },
        {
          signal,
        }
      )

      totalItems = results.estimatedTotalHits
      totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
      hits = results.hits

      // --- Facets ---
      const facetsFilters = resolvedQuery.facetFilter

      // Get the most popular tags
      const { facetHits } = await index.searchForFacetValues(
        {
          facetQuery: '',
          facetName: 'tags',
          filter: facetsFilters,
          q: query,
        },
        {
          signal,
        }
      )

      resultTags = Object.values(facetHits)
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_ASSETS_FILTERS_TAGS)
        .map((facetHit) => facetHit.value)
    }

    const assets = hits.map((asset) =>
      meilisearchAssetToAssetSummary(asset, {
        mainCategorySlug,
        categorySlug,
        subCategorySlug,
        contentSlug,
      })
    )

    // Generate metadata
    const meta: Meta & {
      tags: string[]
    } = {
      current_page: currentPage,
      total_pages: totalPages,
      total_items: totalItems,
      items_per_page: ITEMS_PER_PAGE,
      tags: resultTags,
    }

    console.debug('Found %d assets', hits.length)

    return {
      data: assets,
      meta,
    }
  }

  getAssetByIdOrSlug(params: GetAssetByIdOrSlugParams): Promise<Asset | null> {
    throw new Error('Method not implemented.')
  }
}
